import React, { useEffect } from "react"
import PropTypes from "prop-types"
import LoadingAnimation from "@kit/design/loading-animation"
import { useRequestReducer } from "hooks/use-requests.js"
import { FormData } from "app/forms/data/form-data"
import { Template } from "app/forms/utils/template"
import { stylesToJs } from "app/forms/utils/styles-to-js"
import { addStyles } from "app/forms/utils/add-styles"

const DEFAULT_TEMPLATE_URL = "https://pages.convertkit.com/templates/"

function useTemplate(name, url) {
  const [templateContent, dispatch] = useRequestReducer({
    data: undefined,
    status: "loading"
  })
  const { status, data, error } = templateContent
  useEffect(() => {
    const getTemplate = async () => {
      try {
        const template = Template({ name, url })
        const result = await template.layout()
        if (!result) {
          return dispatch({ type: "EMPTY" })
        }
        dispatch({ type: "RESOLVE", payload: result })
      } catch (error) {
        dispatch({ type: "REJECT", payload: error.message })
      }
    }
    getTemplate()
  }, [name, url, dispatch])

  return [data, status, error]
}

function PreviewTemplate({ name, url, styles, elements }) {
  const templateURL = url ?? DEFAULT_TEMPLATE_URL.concat(name)
  const [Template, status] = useTemplate(name, templateURL)

  const forElement = (name, defaults) => {
    const allStyles = addStyles(name, styles)
    return stylesToJs(name, allStyles, defaults)
  }

  return {
    loading: () => (
      <div className="flex justify-center items-center h-screen w-full flex-col text-center">
        <LoadingAnimation />
        <h2 className="my-4 font-semibold">Loading template....</h2>
      </div>
    ),
    failed: () => (
      <div className="flex justify-center items-center w-full text-gray-700 text-center">
        <div>
          <h2 className="font-bold text-2xl m-4">
            An error occured while trying to load this template
          </h2>
          <p>
            This could be due to visiting an outdated link. Try visiting this
            template from{" "}
            <a href="/pages/new" className="underline">
              our templates index here
            </a>
            .
          </p>
          <p>
            If you are still having any issues, please{" "}
            <a href="https://convertkit.com/support" className="underline">
              contact support
            </a>
            .
          </p>
        </div>
      </div>
    ),
    success: () => (
      <div className="overflow-hidden w-full">
        <FormData
          initialSettings={[]}
          initialPages={[
            {
              id: 0,
              name,
              template: name,
              root_page: true,
              display_type: "page",
              elements: elements,
              styles: styles
            }
          ]}
          editing={false}
          action={""}
          id={0}
          uid="previewPage"
          template={{ name, url: templateURL }}
          format={null}
          ckjsVersion={""}
          metadata={{}}
          url={""}
          htmlUrl={""}
        >
          <Template
            styles={{ all: styles, forElement }}
            mode="preview"
            elements={{
              all: elements,
              inputs: [],
              custom: [],
              remove: () => null,
              update: () => null
            }}
            template={Template}
            uid="previewPage"
          />
        </FormData>
      </div>
    )
  }[status]()
}

PreviewTemplate.propTypes = {
  name: PropTypes.string.isRequired,
  url: PropTypes.string
}

export default PreviewTemplate
